import React, { useState, useRef, useCallback, useEffect } from "react";
import ScaledLogo from "./ScaledLogo";
import styles from "./materials.module.scss";

const AREA_W = 1080;
const AREA_H = 1102;
const MIN_SCALE = 1;
const MAX_SCALE = 4;

const SoMePhotoFrame = ({ imageUrl, event, bgColor, crop, onCropChange }) => {
  const areaRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const dragging = useRef(false);

  const handleLoad = useCallback((e) => {
    setNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
  }, []);

  // Compute display dimensions from natural size + crop
  const getDisplayDims = useCallback(() => {
    if (!naturalSize) return null;
    const scaleX = AREA_W / naturalSize.w;
    const scaleY = AREA_H / naturalSize.h;
    const coverScale = Math.max(scaleX, scaleY);
    const s = (crop?.scale || 1);
    return {
      width: naturalSize.w * coverScale * s,
      height: naturalSize.h * coverScale * s,
    };
  }, [naturalSize, crop?.scale]);

  const clampOffset = useCallback((ox, oy, dims) => {
    if (!dims) return { offsetX: 0, offsetY: 0 };
    return {
      offsetX: Math.min(0, Math.max(-(dims.width - AREA_W), ox)),
      offsetY: Math.min(0, Math.max(-(dims.height - AREA_H), oy)),
    };
  }, []);

  // Get the CSS transform scale between screen pixels and full-size pixels
  const getScreenScale = useCallback(() => {
    if (!areaRef.current) return 1;
    return areaRef.current.getBoundingClientRect().width / AREA_W;
  }, []);

  // Drag handlers
  const handlePointerDown = useCallback((e) => {
    if (!onCropChange) return;
    e.preventDefault();
    dragging.current = true;
    e.target.setPointerCapture(e.pointerId);
  }, [onCropChange]);

  const handlePointerMove = useCallback((e) => {
    if (!dragging.current || !onCropChange) return;
    const screenScale = getScreenScale();
    const dims = getDisplayDims();
    if (!dims) return;
    const dx = e.movementX / screenScale;
    const dy = e.movementY / screenScale;
    const ox = (crop?.offsetX || 0) + dx;
    const oy = (crop?.offsetY || 0) + dy;
    const clamped = clampOffset(ox, oy, dims);
    onCropChange({ ...crop, ...clamped });
  }, [onCropChange, crop, getScreenScale, getDisplayDims, clampOffset]);

  const handlePointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  // Scroll-to-zoom
  const handleWheel = useCallback((e) => {
    if (!onCropChange || !naturalSize) return;
    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const oldScale = crop?.scale || 1;
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, oldScale + delta));
    if (newScale === oldScale) return;

    // Zoom toward cursor position
    const rect = areaRef.current.getBoundingClientRect();
    const screenScale = rect.width / AREA_W;
    // Cursor position in full-size coordinates
    const cursorX = (e.clientX - rect.left) / screenScale;
    const cursorY = (e.clientY - rect.top) / screenScale;

    // Point in image space that's under the cursor
    const imgX = cursorX - (crop?.offsetX || 0);
    const imgY = cursorY - (crop?.offsetY || 0);

    // After scale change, keep that same image point under the cursor
    const ratio = newScale / oldScale;
    const newOx = cursorX - imgX * ratio;
    const newOy = cursorY - imgY * ratio;

    // Compute new display dims with new scale
    const scaleX = AREA_W / naturalSize.w;
    const scaleY = AREA_H / naturalSize.h;
    const coverScale = Math.max(scaleX, scaleY);
    const dims = {
      width: naturalSize.w * coverScale * newScale,
      height: naturalSize.h * coverScale * newScale,
    };
    const clamped = clampOffset(newOx, newOy, dims);
    onCropChange({ scale: newScale, ...clamped });
  }, [onCropChange, crop, naturalSize, clampOffset]);

  // Attach wheel with passive: false
  useEffect(() => {
    const el = areaRef.current;
    if (!el || !onCropChange) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel, onCropChange]);

  const dims = getDisplayDims();
  const ox = crop?.offsetX || 0;
  const oy = crop?.offsetY || 0;

  return (
    <div className={styles.someFrame} style={{ "--mat-bg": bgColor }}>
      <div
        ref={areaRef}
        className={styles.somePhotoArea}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ cursor: onCropChange ? "grab" : "default" }}
      >
        <img
          src={imageUrl}
          alt=""
          className={styles.somePhoto}
          onLoad={handleLoad}
          draggable={false}
          style={dims ? {
            width: dims.width,
            height: dims.height,
            left: ox,
            top: oy,
          } : undefined}
        />
      </div>
      <div className={styles.someBar}>
        <div className={styles.someBarLogo}>
          <ScaledLogo color={event.color} width={128} height={148} />
        </div>
      </div>
    </div>
  );
};

export default SoMePhotoFrame;
